import * as React from "react";

import { HighlightTextAreaContainer, Error } from "./highlight-textarea-style";
import { withDefault } from "collection-utils";
import { assert } from "common/Support";
import { withLabelAndByline } from "designer/lib/with-label";
import debounce from "lodash/debounce";
import AppIcon from "common/app-icon";
import { TokenEntry } from "designer/ui/views/token-entry/token-entry";

type InputValue = string | number | readonly string[] | undefined;

interface Props
    extends React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement> {
    placeholder?: string;
    inputRef?: any;
    error?: string;
    monospace?: boolean;
    debounceUpdate?: boolean;
    minHeight?: number;
    alignTop?: boolean;
    onContextButton?: () => void;
    validate?: (newVal: string) => boolean;
}
interface State {
    focused: boolean;
    lastPropsValue: InputValue;
    debounceValue: InputValue;
}

export default class HighlightTextArea extends React.PureComponent<Props, State> {
    public state: State = {
        focused: false,
        debounceValue: "",
        lastPropsValue: "",
    };

    public static getDerivedStateFromProps(props: Props, state: State) {
        if (props.debounceUpdate === true) {
            if (props.value !== state.lastPropsValue) {
                state.debounceValue = props.value;
            }
            state.lastPropsValue = props.value;
        }
        return state;
    }

    private sendUpdate = debounce((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (this.props.onChange !== undefined) {
            this.props.onChange(e);
        }
    }, 500);

    private onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (this.props.validate !== undefined && !this.props.validate(e.target.value)) {
            return;
        }
        e.persist();
        this.sendUpdate(e);
        this.setState({ debounceValue: e.target.value });
    };

    private onFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
        if (this.props.onFocus !== undefined) {
            this.props.onFocus(event);
        }

        this.setState({ focused: true });
    };

    private onBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
        if (this.props.onBlur !== undefined) {
            this.props.onBlur(event);
        }

        this.setState({ focused: false });
    };

    public render(): React.ReactNode {
        const {
            value,
            ref,
            onFocus,
            onBlur,
            monospace,
            onChange,
            debounceUpdate,
            onContextButton,
            minHeight,
            alignTop,
            ...rest
        } = this.props;

        let useValue = withDefault(value, "");
        let useOnChange = onChange;
        if (debounceUpdate === true) {
            useValue = withDefault(this.state.debounceValue, "");
            useOnChange = this.onChange;
        }

        assert(this.props.onChange !== undefined, "GrowingEntry must be a controlled input area");

        return (
            <>
                <HighlightTextAreaContainer
                    focused={this.state.focused}
                    showContextButton={onContextButton !== undefined}
                    minHeight={minHeight}
                    alignTop={alignTop}
                    font={monospace === true ? "'Roboto Mono', monospace" : undefined}>
                    <TokenEntry
                        {...rest}
                        onFocus={this.onFocus}
                        onBlur={this.onBlur}
                        value={useValue}
                        onChange={useOnChange}
                    />
                    {onContextButton !== undefined && (
                        <AppIcon icon="moreVertical" className="context-button" size={16} onClick={onContextButton} />
                    )}
                </HighlightTextAreaContainer>
                {this.props.error !== undefined && <Error>{this.props.error}</Error>}
            </>
        );
    }
}

export const LabeledHighlightTextArea = withLabelAndByline(HighlightTextArea, "stacked");
